import axios from "axios";

const WEB_PUBSUB_USER_ID_STORAGE_KEY = "OmnioWebPubSubUserId";
export const OMNIO_SESSION_STORAGE_KEY = "OmnioLogin";
const SESSION_IDB_DB_NAME = "OmnioSession";
const SESSION_IDB_STORE_NAME = "session";
const SESSION_IDB_PRIMARY_KEY = "primary";
const TOKEN_RENEWAL_SKEW_MS = 60 * 1000;
let inMemoryFallbackId = null;
let cachedSession = null;
let idbInstance = null;
let idbOpening = null;
let tokenRenewalPromise = null;

const isBrowser = () => typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const safeParseJson = (value) => {
  if (!value || typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn("Failed to parse stored session JSON", error);
    return null;
  }
};

const safeStringifyJson = (value) => {
  try {
    return JSON.stringify(value);
  } catch (error) {
    console.warn("Failed to stringify session JSON", error);
    return null;
  }
};

const createRandomId = () => {
  if (typeof crypto !== "undefined") {
    if (crypto.randomUUID) {
      return crypto.randomUUID();
    }
    if (crypto.getRandomValues) {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    }
  }
  const randomPart = Math.random().toString(36).slice(2, 10);
  return `wp-${Date.now().toString(36)}-${randomPart}`;
};

const readSessionFromLocalStorage = () => {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(OMNIO_SESSION_STORAGE_KEY);
    return safeParseJson(raw);
  } catch (error) {
    console.warn("Unable to read stored session", error);
    return null;
  }
};

const writeSessionToLocalStorage = (session) => {
  if (!isBrowser()) return;
  try {
    if (session == null) {
      window.localStorage.removeItem(OMNIO_SESSION_STORAGE_KEY);
      return;
    }
    const serialized = safeStringifyJson(session);
    if (serialized) {
      window.localStorage.setItem(OMNIO_SESSION_STORAGE_KEY, serialized);
    }
  } catch (error) {
    console.warn("Unable to write stored session", error);
  }
};

const getIndexedDb = () => {
  if (!isBrowser() || typeof window.indexedDB === "undefined") {
    return Promise.resolve(null);
  }

  if (idbInstance) {
    return Promise.resolve(idbInstance);
  }

  if (idbOpening) {
    return idbOpening;
  }

  idbOpening = new Promise((resolve) => {
    try {
      const request = window.indexedDB.open(SESSION_IDB_DB_NAME, 1);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(SESSION_IDB_STORE_NAME)) {
          db.createObjectStore(SESSION_IDB_STORE_NAME);
        }
      };

      request.onsuccess = () => {
        const db = request.result;
        db.onclose = () => {
          idbInstance = null;
          idbOpening = null;
        };
        idbInstance = db;
        resolve(db);
      };

      request.onerror = () => {
        console.warn("Unable to open session IndexedDB", request.error);
        idbOpening = null;
        resolve(null);
      };
    } catch (error) {
      console.warn("Unable to open session IndexedDB", error);
      idbOpening = null;
      resolve(null);
    }
  });

  return idbOpening;
};

const readSessionFromIndexedDb = async () => {
  const db = await getIndexedDb();
  if (!db) return null;

  return new Promise((resolve) => {
    try {
      const tx = db.transaction(SESSION_IDB_STORE_NAME, "readonly");
      const store = tx.objectStore(SESSION_IDB_STORE_NAME);
      const request = store.get(SESSION_IDB_PRIMARY_KEY);
      request.onsuccess = () => {
        resolve(request.result ?? null);
      };
      request.onerror = () => {
        console.warn("Unable to read session from IndexedDB", request.error);
        resolve(null);
      };
    } catch (error) {
      console.warn("Unable to read session from IndexedDB", error);
      resolve(null);
    }
  });
};

const writeSessionToIndexedDb = async (session) => {
  const db = await getIndexedDb();
  if (!db) return;

  await new Promise((resolve) => {
    try {
      const tx = db.transaction(SESSION_IDB_STORE_NAME, "readwrite");
      const store = tx.objectStore(SESSION_IDB_STORE_NAME);
      const request =
        session == null
          ? store.delete(SESSION_IDB_PRIMARY_KEY)
          : store.put(session, SESSION_IDB_PRIMARY_KEY);
      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.warn("Unable to persist session to IndexedDB", request.error);
        resolve();
      };
    } catch (error) {
      console.warn("Unable to persist session to IndexedDB", error);
      resolve();
    }
  });
};

export const getStoredSession = () => {
  if (cachedSession) return cachedSession;
  const session = readSessionFromLocalStorage();
  if (session) {
    cachedSession = session;
  }
  return session;
};

export const hydrateStoredSession = async () => {
  const existing = getStoredSession();
  if (existing) return existing;

  const fromIndexedDb = await readSessionFromIndexedDb();
  if (fromIndexedDb) {
    cachedSession = fromIndexedDb;
    writeSessionToLocalStorage(fromIndexedDb);
  }
  return cachedSession;
};

export const persistStoredSession = async (session) => {
  cachedSession = session ?? null;
  writeSessionToLocalStorage(session);
  await writeSessionToIndexedDb(session);
  return cachedSession;
};

export const clearStoredSession = async () => {
  cachedSession = null;
  writeSessionToLocalStorage(null);
  await writeSessionToIndexedDb(null);
};

const coreApiUrl = (path) => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const configured = process.env.REACT_APP_CORE_API_BASE_URL || "";
  if (configured) return `${configured.replace(/\/$/, "")}${normalizedPath}`;
  const fallbackBase = process.env.NODE_ENV === "development" ? "" : "/api";
  return `${fallbackBase}${normalizedPath}`;
};

const authTokenUrl = () => {
  if (process.env.NODE_ENV === "development") return "/token";
  const configured = process.env.REACT_APP_AUTH_TOKEN_URL || "";
  return configured || coreApiUrl("/token");
};

const getConfiguredAuthTokenUsername = () =>
  process.env.REACT_APP_AUTH_TOKEN_USERNAME || process.env.REACT_APP_AUTH_SERVICE_USERNAME || "";

const getConfiguredAuthTokenPassword = () =>
  process.env.REACT_APP_AUTH_TOKEN_PASSWORD || process.env.REACT_APP_AUTH_SERVICE_PASSWORD || "";

export const getAuthTokenCredentials = (username, password) => {
  const configuredUsername = getConfiguredAuthTokenUsername();
  const configuredPassword = getConfiguredAuthTokenPassword();
  const fromEnvironment = Boolean(configuredUsername && configuredPassword);

  return {
    username: fromEnvironment ? configuredUsername : username,
    password: fromEnvironment ? configuredPassword : password,
    fromEnvironment,
  };
};

export const requestAccessToken = async ({ username, password }) => {
  const response = await axios.post(
    authTokenUrl(),
    { username, password },
    {
      headers: { "Content-Type": "application/json" },
      omnioOffline: { skipQueue: true, skipCache: true, skipAuthHeader: true },
      __omnioSkipAuthHeader: true,
      __omnioTokenRefresh: true,
    }
  );

  return {
    accessToken: response.data?.accessToken ?? response.data?.access_token ?? "",
    expiresAt: response.data?.expiresAt ?? response.data?.expires_at ?? null,
    raw: response.data,
  };
};

export const getSessionAccessToken = (session) =>
  session?.accessToken || session?.token || session?.access_token || "";

const getSessionTokenExpiry = (session) => session?.expiresAt || session?.tokenExpiresAt || null;

export const isAccessTokenExpired = (session, skewMs = TOKEN_RENEWAL_SKEW_MS) => {
  const expiresAt = getSessionTokenExpiry(session);
  if (!expiresAt) return false;
  const expiresAtMs = Date.parse(expiresAt);
  if (!Number.isFinite(expiresAtMs)) return false;
  return Date.now() + skewMs >= expiresAtMs;
};

const getRenewalCredentials = (session) => {
  const configured = getAuthTokenCredentials("", "");
  if (configured.fromEnvironment) return configured;

  if (session?.authTokenUsername && session?.authTokenPassword) {
    return {
      username: session.authTokenUsername,
      password: session.authTokenPassword,
      fromEnvironment: false,
    };
  }

  return null;
};

export const renewStoredAccessToken = async () => {
  if (tokenRenewalPromise) return tokenRenewalPromise;

  tokenRenewalPromise = (async () => {
    const session = getStoredSession() ?? (await hydrateStoredSession());
    const credentials = getRenewalCredentials(session);
    if (!credentials?.username || !credentials?.password) {
      throw new Error("No stored token credentials are available to renew the access token");
    }

    const tokenResult = await requestAccessToken(credentials);
    if (!tokenResult.accessToken) {
      throw new Error("Token renewal response did not include an accessToken");
    }

    const nextSession = {
      ...(session || {}),
      token: tokenResult.accessToken,
      accessToken: tokenResult.accessToken,
      expiresAt: tokenResult.expiresAt,
      tokenExpiresAt: tokenResult.expiresAt,
    };

    if (!credentials.fromEnvironment) {
      nextSession.authTokenUsername = credentials.username;
      nextSession.authTokenPassword = credentials.password;
    }

    return persistStoredSession(nextSession);
  })();

  try {
    return await tokenRenewalPromise;
  } finally {
    tokenRenewalPromise = null;
  }
};

export const ensureValidAccessToken = async () => {
  const session = getStoredSession() ?? (await hydrateStoredSession());
  if (!session) return "";

  if (getSessionAccessToken(session) && !isAccessTokenExpired(session)) {
    return getSessionAccessToken(session);
  }

  const renewedSession = await renewStoredAccessToken();
  return getSessionAccessToken(renewedSession);
};

const deriveRolesFromSession = (session) => {
  if (!session) return [];

  if (Array.isArray(session.roles)) {
    return session.roles.filter((role) => typeof role === "string" && role.trim().length > 0);
  }

  if (typeof session.roles === "string" && session.roles.trim().length > 0) {
    return [session.roles.trim()];
  }

  if (session.userTypeID != null) {
    return [`userType:${session.userTypeID}`];
  }

  return [];
};

const deriveGroupsFromSession = (session) => {
  if (!session) return [];

  if (Array.isArray(session.groups)) {
    return session.groups.filter((group) => typeof group === "string" && group.trim().length > 0);
  }

  if (typeof session.group === "string" && session.group.trim().length > 0) {
    return [session.group.trim()];
  }

  return [];
};

const fallbackIdFromSession = (session) => {
  if (!session) return null;
  if (session.ID != null) {
    return `session-${session.ID}`;
  }
  if (typeof session.userName === "string" && session.userName.trim().length > 0) {
    return `user-${session.userName.trim()}`;
  }
  return null;
};

export const getPersistentWebPubSubUserId = () => {
  if (inMemoryFallbackId) return inMemoryFallbackId;
  if (!isBrowser()) return null;

  try {
    const existing = window.localStorage.getItem(WEB_PUBSUB_USER_ID_STORAGE_KEY);
    if (existing && existing.trim().length > 0) {
      return existing;
    }

    const minted = createRandomId();
    window.localStorage.setItem(WEB_PUBSUB_USER_ID_STORAGE_KEY, minted);
    return minted;
  } catch (error) {
    console.warn("Unable to access localStorage for WebPubSub user id", error);
    inMemoryFallbackId = createRandomId();
    return inMemoryFallbackId;
  }
};

const uniqueStrings = (values) => {
  const seen = new Set();
  const result = [];
  values.forEach((value) => {
    if (typeof value !== "string") return;
    const trimmed = value.trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    result.push(trimmed);
  });
  return result;
};

const normalizeGroupsInput = (input) => {
  if (input == null) return [];
  if (Array.isArray(input)) return input;
  return [input];
};

const HUB_NAME = process.env.REACT_APP_WEBPUBSUB_HUB || "omnio";
const DEFAULT_WEB_PUBSUB_ROLES = [
  "webpubsub.joinLeaveGroup",
  `webpubsub.joinLeaveGroup.${HUB_NAME}`,
];

export const buildWebPubSubNegotiatePayload = (primaryGroup, additionalGroups = []) => {
  const session = getStoredSession();
  let userId = getPersistentWebPubSubUserId();

  if (!userId) {
    userId = fallbackIdFromSession(session) ?? createRandomId();
    inMemoryFallbackId = userId;
  }

  const sessionGroups = deriveGroupsFromSession(session);
  const negotiatedGroups = uniqueStrings([
    ...(primaryGroup ? [primaryGroup] : []),
    ...normalizeGroupsInput(additionalGroups),
    ...sessionGroups,
  ]);

  const roles = uniqueStrings([...deriveRolesFromSession(session), ...DEFAULT_WEB_PUBSUB_ROLES]);

  const payload = { userId };

  if (primaryGroup) {
    payload.group = primaryGroup;
  }

  if (roles.length > 0) {
    payload.roles = roles;
  }

  if (negotiatedGroups.length > 0) {
    payload.groups = negotiatedGroups;
  }

  return payload;
};

try {
  if (isBrowser()) {
    window.__omnioSessionDebug = {
      getStoredSession,
      hydrateStoredSession,
      persistStoredSession,
      clearStoredSession,
    };
  }
} catch {
  // no-op: debug hook is best effort only
}
